import { Button, Card, CardContent, FormField, Input, PageHeader, SwitchField } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  useOwnerState,
  useToggleVenueFeature,
  useUpdateVenue,
} from "../../features/backoffice/queries.ts";

export const Route = createFileRoute("/_app/venue")({ component: VenueScreen });

function VenueScreen() {
  const { data } = useOwnerState();
  const venue = data?.venues.find((item) => item.id === data.currentVenueId);
  const update = useUpdateVenue();
  const toggle = useToggleVenueFeature();
  const [draft, setDraft] = useState({
    name: "Smoke & Mirrors",
    nameTh: "สโมค แอนด์ มิเรอร์",
    description: "",
    logoUrl: "",
    coverUrl: "",
  });
  useEffect(() => {
    if (!venue) return;
    setDraft({
      name: venue.name,
      nameTh: venue.nameTh,
      description: venue.description,
      logoUrl: venue.logoUrl ?? "",
      coverUrl: venue.coverUrl ?? "",
    });
  }, [venue]);

  if (!venue) return <PageHeader title="Venue" subtitle="Loading venue…" />;
  const setField = (key: keyof typeof draft, value: string) =>
    setDraft((current) => ({ ...current, [key]: value }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Venue profile + feature flags"
        subtitle="Owner rules restrict only; user privacy always wins."
      />
      <Card>
        <CardContent className="grid gap-4 p-5 md:grid-cols-2">
          <FormField id="venueName" label="Name EN">
            <Input
              id="venueName"
              value={draft.name}
              onChange={(e) => setField("name", e.target.value)}
            />
          </FormField>
          <FormField id="venueNameTh" label="Name TH">
            <Input
              id="venueNameTh"
              value={draft.nameTh}
              onChange={(e) => setField("nameTh", e.target.value)}
            />
          </FormField>
          <FormField id="line" label="LINE OA linkage">
            <Input id="line" value={venue.lineOaId} readOnly />
          </FormField>
          <FormField id="description" label="Description">
            <Input
              id="description"
              value={draft.description}
              onChange={(e) => setField("description", e.target.value)}
            />
          </FormField>
          <FormField
            id="logoUrl"
            label="Logo placeholder"
            hint="Mock media placeholder now; swap for direct-upload asset key after backend media exists."
          >
            <Input
              id="logoUrl"
              value={draft.logoUrl}
              placeholder="mock://brand/logo-placeholder"
              onChange={(e) => setField("logoUrl", e.target.value)}
            />
          </FormField>
          <FormField
            id="coverUrl"
            label="Cover placeholder"
            hint="Shown to owners so setup depth includes visual identity before upload APIs."
          >
            <Input
              id="coverUrl"
              value={draft.coverUrl}
              placeholder="mock://brand/cover-placeholder"
              onChange={(e) => setField("coverUrl", e.target.value)}
            />
          </FormField>
          <div className="grid gap-3 md:col-span-2 md:grid-cols-[10rem_1fr]">
            <MediaPlaceholder label="Logo" value={draft.logoUrl} compact />
            <MediaPlaceholder label="Cover" value={draft.coverUrl} />
          </div>
          <div className="md:col-span-2">
            <Button onClick={() => update.mutate({ id: venue.id, ...draft })}>Save venue</Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="grid gap-3 p-5 md:grid-cols-2">
          {Object.entries(venue.featureFlags).map(([key, enabled]) => (
            <SwitchField
              key={key}
              label={key}
              description="Disabling cascades to branch overrides and cannot force a user visible."
              checked={enabled}
              onChange={(checked) =>
                toggle.mutate({ key: key as keyof typeof venue.featureFlags, enabled: checked })
              }
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function MediaPlaceholder({
  label,
  value,
  compact = false,
}: {
  label: string;
  value: string;
  compact?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border border-dashed border-primary/40 bg-primary/5 p-4 ${
        compact ? "min-h-28" : "min-h-28"
      }`}
    >
      <p className="font-medium text-sm">{label} preview</p>
      <p className="mt-2 break-all text-muted-foreground text-xs">
        {value || `No ${label.toLowerCase()} placeholder set`}
      </p>
    </div>
  );
}
